/** Pure planning calculations. No storage, network, tax estimates or ledger writes. */
export type Frequency = 'week' | 'fortnight' | 'month' | 'quarter' | 'year' | 'once';
export type IncomeStream = {
  id: string; version?: string; name: string; recipient: string; kind: string; cents: number;
  frequency: Frequency; firstOn: string; lastOn: string | null; estimated: boolean;
  payslip?: { gross: number | null; tax: number | null; super: number | null; sacrifice: number | null };
};
export type Payment = { id: string; streamId: string; on: string; cents: number; estimated: boolean; name: string };
const DAY = 86400000;
export function dateNumber(s: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new Error('Enter a valid date.');
  const n = Date.parse(s+'T00:00:00Z');
  if (!Number.isFinite(n) || new Date(n).toISOString().slice(0,10) !== s) throw new Error('Enter a valid date.');
  return n;
}
export const dateText = (n:number) => new Date(n).toISOString().slice(0,10);
export const plusDays = (s:string,n:number) => dateText(dateNumber(s)+n*DAY);
export function monday(s:string) { const n=dateNumber(s); return dateText(n-((new Date(n).getUTCDay()+6)%7)*DAY); }
export function inputCents(s:string):number|null {
  const clean=s.trim().replace(/^\$/,'').replace(/,/g,'');
  if(!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  const [whole,fraction='']=clean.split('.');const n=Number(whole)*100+Number(fraction.padEnd(2,'0'));
  return Number.isSafeInteger(n)&&n<=100000000000?n:null;
}
export function validateStream(s:IncomeStream) {
  if(!s.name.trim()) throw new Error('Give this income stream a name.');
  if(!Number.isSafeInteger(s.cents)||s.cents<=0||s.cents>100000000000) throw new Error('Enter a positive take-home amount with no more than two decimal places.');
  const start=dateNumber(s.firstOn);
  if(s.lastOn && dateNumber(s.lastOn)<start) throw new Error('End date must be on or after the first payment.');
  if(!['week','fortnight','month','quarter','year','once'].includes(s.frequency)) throw new Error('Choose a payment frequency.');
  if(s.payslip && Object.values(s.payslip).some(n=>n!==null&&(!Number.isSafeInteger(n)||n<0||n>100000000000))) throw new Error('Payslip details must be valid non-negative amounts.');
}
function occurrence(s:IncomeStream,index:number) {
  const start=dateNumber(s.firstOn);
  if(s.frequency==='week'||s.frequency==='fortnight') return dateText(start+index*(s.frequency==='week'?7:14)*DAY);
  if(s.frequency==='once') return s.firstOn;
  const d=new Date(start);const month=d.getUTCMonth()+index*({month:1,quarter:3,year:12}[s.frequency]);
  // Always use the original day: 31 Jan -> 28 Feb -> 31 Mar, not 28 Mar.
  const last=new Date(Date.UTC(d.getUTCFullYear(),month+1,0)).getUTCDate();
  return dateText(Date.UTC(d.getUTCFullYear(),month,Math.min(d.getUTCDate(),last)));
}
export function paymentsBetween(streams:IncomeStream[],from:string,to:string):Payment[] {
  if(dateNumber(to)<dateNumber(from)) throw new Error('Invalid forecast range.');
  if(dateNumber(to)-dateNumber(from)>366*DAY) throw new Error('Forecast range must be no longer than a year.');
  const result:Payment[]=[];
  for(const s of streams) {
    validateStream(s);
    let index=0;
    if(s.frequency==='week'||s.frequency==='fortnight') index=Math.max(0,Math.floor((dateNumber(from)-dateNumber(s.firstOn))/(DAY*(s.frequency==='week'?7:14))));
    else if(s.frequency!=='once') {
      const a=new Date(dateNumber(s.firstOn)),b=new Date(dateNumber(from));
      index=Math.max(0,Math.floor(((b.getUTCFullYear()-a.getUTCFullYear())*12+b.getUTCMonth()-a.getUTCMonth())/({month:1,quarter:3,year:12}[s.frequency])));
    }
    for(let guard=0;guard<370;guard++,index++) {
      const on=occurrence(s,index);
      if(on>to||(s.lastOn&&on>s.lastOn)) break;
      if(on>=from) result.push({id:`${s.id}:${on}`,streamId:s.id,on,cents:s.cents,estimated:s.estimated,name:s.name});
      if(s.frequency==='once') break;
    }
  }
  return result.sort((a,b)=>a.on.localeCompare(b.on)||a.name.localeCompare(b.name));
}
export function weeklyAverage(s:IncomeStream) {
  // Consistent with existing budget-entry conventions (52 weeks/26 fortnights).
  return s.frequency==='once'?0:Math.round(s.cents*({week:52,fortnight:26,month:12,quarter:4,year:1}[s.frequency])/52);
}
export type BudgetCategory={id:string;kind:string;is_group:boolean;archived_at:string|null;monthly_budget_cents:number|null};
export function weeklyBudget(categories:BudgetCategory[]) {
  return Math.round(categories.filter(c=>c.kind==='expense'&&!c.is_group&&!c.archived_at).reduce((sum,c)=>sum+(c.monthly_budget_cents??0),0)*12/52);
}
export type ActualTransaction={id:string;posted_on:string;status:string;transfer_pair_id:string|null;transaction_splits:{amount_cents:number;category_id:string}[]};
export function actualTotals(transactions:ActualTransaction[],categories:BudgetCategory[]) {
  const kinds=new Map(categories.map(c=>[c.id,c.kind]));let spent=0,received=0,uncoded=0;
  for(const t of transactions) {
    if(t.status==='excluded'||t.transfer_pair_id) continue;
    if(t.status!=='coded'){uncoded++;continue;}
    for(const s of t.transaction_splits){if(kinds.get(s.category_id)==='expense')spent-=s.amount_cents;else if(kinds.get(s.category_id)==='income')received+=s.amount_cents;}
  }
  return {spent,received,uncoded};
}
